import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { QrCode, X, Camera, AlertCircle, Keyboard, Send } from 'lucide-react';

export const QRScanner = ({ onScan, onClose }) => {
  const [error, setError] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [activeTab, setActiveTab] = useState('camera');
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  useEffect(() => {
    if (activeTab === 'camera') {
      startScanner();
    }
    return () => {
      stopScanner();
    };
  }, [activeTab]);

  const startScanner = async () => {
    try {
      setError(null);
      setIsScanning(true);
      
      const html5QrCode = new Html5Qrcode("qr-reader");
      html5QrCodeRef.current = html5QrCode;

      const config = { 
        fps: 10, 
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0
      };

      await html5QrCode.start(
        { facingMode: "environment" },
        config,
        (decodedText) => {
          console.log("QR Code scanned:", decodedText);
          stopScanner();
          onScan(decodedText);
        },
        (errorMessage) => {
          // Ignore scan errors (no QR found)
        }
      );
    } catch (err) {
      console.error("Scanner error:", err);
      // Switch to manual tab if camera fails
      setError("Impossible d'accéder à la caméra. Utilisez la saisie manuelle.");
      setIsScanning(false);
      setActiveTab('manual');
    }
  };

  const stopScanner = async () => {
    try {
      if (html5QrCodeRef.current && html5QrCodeRef.current.isScanning) {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current.clear();
      }
    } catch (err) {
      console.error("Error stopping scanner:", err);
    }
    setIsScanning(false);
  };

  const handleClose = () => {
    stopScanner();
    onClose();
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (manualCode.trim()) {
      onScan(manualCode.trim());
    }
  };

  const handleTabChange = (value) => {
    if (value === 'manual') {
      stopScanner();
    }
    setActiveTab(value);
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="relative">
          <CardTitle className="font-heading flex items-center gap-2">
            <QrCode className="w-5 h-5" />
            Scanner votre badge
          </CardTitle>
          <Button
            onClick={handleClose}
            variant="ghost"
            size="icon"
            className="absolute right-4 top-4"
          >
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="camera" className="flex items-center gap-2">
                <Camera className="w-4 h-4" />
                Caméra
              </TabsTrigger>
              <TabsTrigger value="manual" className="flex items-center gap-2">
                <Keyboard className="w-4 h-4" />
                Saisie manuelle
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="camera" className="space-y-4">
              {error ? (
                <div className="flex flex-col items-center gap-4 py-8">
                  <div className="p-3 bg-red-100 rounded-full">
                    <AlertCircle className="w-8 h-8 text-red-600" />
                  </div>
                  <p className="text-center text-red-600 text-sm">{error}</p>
                  <Button onClick={startScanner} variant="outline" className="rounded-full">
                    <Camera className="w-4 h-4 mr-2" />
                    Réessayer
                  </Button>
                </div>
              ) : (
                <>
                  <div 
                    id="qr-reader" 
                    ref={scannerRef}
                    className="w-full rounded-lg overflow-hidden bg-gray-900"
                    style={{ minHeight: '300px' }}
                  />
                  {isScanning && (
                    <p className="text-center text-sm text-muted-foreground">
                      Positionnez le QR code devant la caméra
                    </p>
                  )}
                </>
              )}
            </TabsContent>
            
            <TabsContent value="manual" className="space-y-4">
              <form onSubmit={handleManualSubmit} className="space-y-4">
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    Entrez votre numéro de matricule ou scannez avec une scanette USB :
                  </p>
                  <Input
                    type="text"
                    value={manualCode}
                    onChange={(e) => setManualCode(e.target.value)}
                    placeholder="Ex: EMP001 ou 0902937"
                    className="text-lg font-mono text-center"
                    autoFocus
                  />
                </div>
                <Button type="submit" className="w-full rounded-full" disabled={!manualCode.trim()}>
                  <Send className="w-4 h-4 mr-2" />
                  Se connecter
                </Button>
              </form>
              <div className="text-center text-xs text-muted-foreground pt-4 border-t">
                💡 Astuce : Avec une scanette USB, scannez directement le badge - le code sera saisi automatiquement
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default QRScanner;
